import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useCallback, useDebugValue, useEffect, useMemo, useRef } from 'react';
import { store } from '../../redux/store';
import { objectToArray } from '../../utils/objectUtils';

export interface Method {
    name: string;
    handler(data: unknown): void;
}

interface HookReturnType {
    start(): void;
    stop(): void;
    state: HubConnectionState;
}

function useSignalrHub(baseUrl: string, params: Record<string, string[]>, methods: Method[]): HookReturnType {
    // refs are used to make start, stop hook return functions static
    const methodsRef = useRef<Method[]>([]);
    const connectionRef = useRef<HubConnection>();

    useDebugValue(undefined, () => `${connectionRef.current?.state}: ${baseUrl}`);

    const url = useMemo(
        () => {
            const queryParams = objectToArray(params, (key, value) => `${key}=${value.join(',')}`).join('&');
            return `${baseUrl}?${queryParams}`;
        },
        [baseUrl, params],
    );

    const startConnection = useCallback(
        (conn?: HubConnection) => {
            if (conn && conn.state === HubConnectionState.Disconnected) {
                conn.start()
                    .then(() => methodsRef.current.forEach(({ name, handler }) => conn.on(name, handler)))
                    // eslint-disable-next-line no-console
                    .catch(e => console.error('Connection failed: ', e));
            }
        },
        [],
    );

    // technically static
    const start = useCallback(
        () => startConnection(connectionRef.current),
        [startConnection],
    );

    const stop = useCallback(
        () => connectionRef.current?.stop(),
        [],
    );

    useEffect(
        () => {
            let newConnection: HubConnection;
            const currentConn = connectionRef.current;

            if (currentConn?.baseUrl !== url || methodsRef.current !== methods) {
                newConnection = new HubConnectionBuilder()
                    .withUrl(url, { accessTokenFactory: () => store.getState().authState.token ?? 'null' })
                    .withAutomaticReconnect()
                    .build();

                methodsRef.current = methods;
                connectionRef.current = newConnection;

                // if there is already a running connection automatically start the new one
                if (currentConn && currentConn.state !== HubConnectionState.Disconnected && currentConn.state !== HubConnectionState.Disconnecting)
                    startConnection(newConnection);
            }

            return () => { newConnection?.stop(); };
        },
        [methods, startConnection, url],
    );

    return { start, stop, state: connectionRef.current?.state ?? HubConnectionState.Disconnected };
}

export default useSignalrHub;